import React, { useState, useRef, useEffect } from 'react';
import ExpandableFormList from "../gdk/ExpandableForm/ExpandableFormList";
import ExpandableFormListItem from "../gdk/ExpandableForm/ExpandableFormListItem";
import EmailForm from './EmailForm';
import { updateSweepDetails } from "../../api/agency-profile";
import { logDetails } from "../../api/logger-configuration";
import DeleteEmailModal from './DeleteEmailModal';
import { GdkExpandableForm } from '@gdk/expandable-form';
import Toast, { ToastTypes } from "../gdk/Toast/Toast";
import PropTypes from 'prop-types';

const EmailManagement = ({ agencyProfile, setAgencyProfile, isSweepsOnboarding = false }) => {

    const [sweepEmailExpandableForm, setSweepEmailExpandableForm] = useState();
    const gdkExpandableFormRef = useRef();
    const [showDeleteAgencyEmailModal, setShowDeleteAgencyEmailModal] = useState(false);
    const [emailToDelete, setEmailToDelete] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Helper function to get valid additional emails
    const getValidAdditionalEmails = () => {
        const additionalEmails = agencyProfile?.sweepDetails?.AdditionalSweepEmails;
        return Array.isArray(additionalEmails)
            ? additionalEmails.filter(emailObj => emailObj?.emailAddress)
            : [];
    };

    // Helper function to check if we should show the "Add Email" button
    const shouldShowAddEmailButton = () => {
        const additionalEmails = agencyProfile?.sweepDetails?.AdditionalSweepEmails;
        const isSweepsEnrolled = agencyProfile?.sweepDetails?.IsSweepsEnrolled;

        // Don't show "Add Email" if not enrolled in sweeps and show when user is onboarding
        if (!isSweepsEnrolled && !isSweepsOnboarding) {
            return false;
        }

        return !additionalEmails ||
            !Array.isArray(additionalEmails) ||
            additionalEmails.length < 5;
    };

    // Only initialize GDK form when there are fewer than 5 additional emails
    useEffect(() => {
        if (shouldShowAddEmailButton()) {
            gdkExpandableFormRef.current = new GdkExpandableForm({ content: "#additional-email-expandable-form-id" });
        }
    }, []);

    const updateConfirmationToastRef = useRef(null);
    const addConfirmationToastRef = useRef(null);
    const deleteConfirmationToastRef = useRef(null);


    const handleFormAction = (formId = null, isSave = false) => {
        if (formId) {
            // Close specific email form (primary or additional emails)
            const formElement = document.getElementById(formId);
            if (formElement && sweepEmailExpandableForm?.closeExpandedForm) {
                sweepEmailExpandableForm.closeExpandedForm(formElement);
            }
            if (isSave && updateConfirmationToastRef.current) {
                updateConfirmationToastRef.current.show();
            }
        } else {
            // Close the "Add Email" form directly using the specific li element
            const addEmailLiElement = document.querySelector("#additional-email-expandable-form-id li.add-state");
            if (addEmailLiElement && gdkExpandableFormRef.current?.closeExpandedForm) {
                gdkExpandableFormRef.current.closeExpandedForm(addEmailLiElement);
            }
            if (isSave && addConfirmationToastRef.current) {
                addConfirmationToastRef.current.show();
            }
        }
    };

    const submitSuccessCallback = (updatedAgencyProfile) => {
        setAgencyProfile(updatedAgencyProfile);
        deleteConfirmationToastRef.current.show();
        logDetails("Sucessfully updated sweep email for agency:" + updatedAgencyProfile.agencyId);
    };

    const submitFinallyCallback = () => {
        setIsDeleting(false);
    }

    const handleSave = (formId = null) => {
        handleFormAction(formId, true);
    };

    const handleCancel = (formId = null) => {
        handleFormAction(formId, false);
    };

    const handleDelete = (formId) => () => {
        // Extract the index from the formId (e.g., "additional-email-form-0" -> 0)
        const index = parseInt(formId.split('-').pop());

        setEmailToDelete(agencyProfile?.sweepDetails?.AdditionalSweepEmails?.[index]?.emailAddress || '');
        setShowDeleteAgencyEmailModal(true);
    };

    const handleConfirmDelete = () => {
        if (emailToDelete && agencyProfile?.sweepDetails?.AdditionalSweepEmails) {
            setIsDeleting(true);

            // Create updated agency profile without the deleted email
            const updatedAdditionalEmails = agencyProfile.sweepDetails.AdditionalSweepEmails.filter(
                (email) => email.emailAddress !== emailToDelete
            );

            const updatedAgencyProfile = {
                ...agencyProfile,
                ivans: agencyProfile.ivans ?? {},
                sweepDetails: {
                    ...agencyProfile.sweepDetails,
                    AdditionalSweepEmails: updatedAdditionalEmails
                }
            };

            // Update the agency profile
            try {
                updateSweepDetails(
                    updatedAgencyProfile,
                    () => submitSuccessCallback(updatedAgencyProfile),
                    submitFinallyCallback
                );
            } catch (error) {
                logDetails("Error updating sweep details: " + error.message);
                setIsDeleting(false);
            }

            // Close the modal
            setShowDeleteAgencyEmailModal(false);
        }
    };

    return (
        <div>
            {updateConfirmationToastRef && (
                <Toast
                    toastRef={updateConfirmationToastRef}
                    header="Thanks!"
                    description="Email address has been updated"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            {addConfirmationToastRef && (
                <Toast
                    toastRef={addConfirmationToastRef}
                    header="Thanks!"
                    description="Additional email address has been added"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            {deleteConfirmationToastRef && (
                <Toast
                    toastRef={deleteConfirmationToastRef}
                    header="Email Deleted"
                    description="Email address has been removed"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            <div>
                {agencyProfile?.sweepDetails?.SweepsEmail && (
                    <ExpandableFormList setExpandableForm={setSweepEmailExpandableForm}>
                        {/* Primary Email */}
                        <ExpandableFormListItem
                            id="primary-email-form"
                            headline="Primary Email"
                            editable={true}
                            emailAddress={agencyProfile?.sweepDetails?.SweepsEmail}
                            form={<EmailForm
                                onSave={() => handleSave("primary-email-form")}
                                onCancel={() => handleCancel("primary-email-form")}
                                agencyProfile={agencyProfile}
                                setAgencyProfile={setAgencyProfile}
                            />}
                        />
                    </ExpandableFormList>
                )}
                {/* Additional Emails */}
                {getValidAdditionalEmails().map((emailObj, index) => (
                    <ExpandableFormList
                        setExpandableForm={setSweepEmailExpandableForm}
                        key={emailObj.emailAddress || `additional-email-${index}`}
                    >
                        <ExpandableFormListItem
                            id={`additional-email-form-${index}`}
                            headline={`Additional Email ${index + 1}`}
                            editable={true}
                            deletable={true}
                            onDelete={handleDelete(`additional-email-form-${index}`)}
                            emailAddress={emailObj?.emailAddress}
                            form={
                                <EmailForm
                                    onSave={() => handleSave(`additional-email-form-${index}`)}
                                    onCancel={() => handleCancel(`additional-email-form-${index}`)}
                                    agencyProfile={agencyProfile}
                                    setAgencyProfile={setAgencyProfile}
                                    isAdditionalEmail={true}
                                    additionalEmailIndex={index}
                                />
                            }
                        />
                    </ExpandableFormList>
                ))}
                {agencyProfile && shouldShowAddEmailButton() && (
                    <ul id="additional-email-expandable-form-id" className="expandable-form">
                        <li className="add-state">
                            <div className="expandable-form-headline">
                                <div className="left-side">
                                    <p className="h4">Add Email Address <gds-tooltip>Add an optional email address to receive sweep account notifications. This doesn't replace the primary email on file unless selected</gds-tooltip></p>
                                </div>
                                <div className="right-side"><span className="expandable-form-control expandable-form-add-icon geico-icon--small geico-icon--actionable geico-icon icon-expand"></span></div>
                            </div>
                            <div className="expandable-form-content-container">
                                {<EmailForm
                                    onSave={() => handleSave()}
                                    onCancel={() => handleCancel()}
                                    agencyProfile={agencyProfile}
                                    setAgencyProfile={setAgencyProfile}
                                    isNewEmail={true}
                                />}
                            </div>
                        </li>
                    </ul>
                )}
            </div>
            <DeleteEmailModal
                emailAddress={emailToDelete}
                showModal={showDeleteAgencyEmailModal}
                setShowModal={setShowDeleteAgencyEmailModal}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                modalId={"delete-email-modal"}
            />
        </div >

    );
};
EmailManagement.propTypes = {
    agencyProfile: PropTypes.object.isRequired,
    setAgencyProfile: PropTypes.func.isRequired,
};

export default EmailManagement;
